import Component from '@glimmer/component';
import { service } from '@ember/service';
import type { TOC } from '@ember/component/template-only';
import { tracked } from '@glimmer/tracking';
import { and, not } from 'tio-ui/utilities';

import { t, type IntlService } from 'ember-intl';
import { trackedFunction } from 'reactiveweb/function';
import { PhSealCheck, PhCurrencyCircleDollar, PhTrash, PhPencil } from 'ember-phosphor-icons';
import Button from 'tio-ui/components/button';
import Chip from 'tio-ui/components/chip';
import Modal from 'tio-ui/components/modal';
import { VStack } from 'tio-ui/components/layout';
import Card from 'tio-ui/components/card';
import type { CompanySettingExtension as CompanySettingModel } from 'tio-data/data/resources/company-setting.ext';
import type { CompanyLpnSettingExtension as CompanyLpnSetting } from 'tio-data/data/resources/company-lpn-setting.ext';
import type { LpnPageExtension as LpnPage } from 'tio-data/data/resources/lpn-page.ext';
import type RouterService from '@ember/routing/router-service';
import type { Store as StoreService } from '@warp-drive/core';
import { LinkTo } from '@ember/routing';
import { deleteExistingRecord } from 'tio-data/requests/save-record';
import { buildLpnCardView, lpnBadges, lpnDescriptionHtml } from '../../utils/lpn/card-view.ts';

interface LpnCardSignature {
  Args: {
    lpn: LpnPage;
    companySettings?: CompanySettingModel;
    isAdminView?: boolean;
  };
  Element: HTMLElement;
  Blocks: Record<string, never>;
}

const ConditionalLink: TOC<{
  Args: { href?: string; isAdminView?: boolean };
  Blocks: { default: [] };
}> = <template>
  {{#if (and (not @isAdminView) @href)}}
    <a href={{@href}} target="_blank" rel="noopener noreferrer" class="block h-full no-underline">
      {{yield}}
    </a>
  {{else}}
    {{yield}}
  {{/if}}
</template>;

export default class LpnCard extends Component<LpnCardSignature> {
  @service declare intl: IntlService;
  @service declare router: RouterService;
  @service declare store: StoreService;

  @tracked isDeleteModalOpen = false;
  @tracked isDeleting = false;

  openDeleteModal = () => {
    this.isDeleteModalOpen = true;
  };

  closeDeleteModal = () => {
    this.isDeleteModalOpen = false;
  };

  deletePartner = async () => {
    try {
      this.isDeleting = true;
      await deleteExistingRecord(this.store, this.args.lpn);
      this.store.unloadRecord(this.args.lpn);
      this.router.transitionTo('authenticated.lpn.index');
    } catch (error) {
      this.isDeleting = false;
      console.error('Error deleting partner:', error);
    }
  };

  companyLpnSettingState = trackedFunction(this, async () => {
    return this.args.lpn.settingForCompany(this.args.companySettings);
  });

  get companyLpnSetting() {
    return this.companyLpnSettingState.value as CompanyLpnSetting | undefined;
  }

  get cardView() {
    return buildLpnCardView(this.args.lpn, this.companyLpnSetting);
  }

  get badges() {
    return lpnBadges(this.cardView, this.intl);
  }

  get topBadge() {
    return this.badges.topBadge;
  }

  get tags() {
    return this.badges.tags;
  }

  get providedBy() {
    return this.cardView.providedBy?.trim();
  }

  get descriptionHtml() {
    return lpnDescriptionHtml(this.args.lpn, this.companyLpnSetting);
  }

  <template>
    <ConditionalLink @isAdminView={{@isAdminView}} @href={{this.cardView.url}}>
      <Card @radius="card" @elevation="card" @padding="sm" class="h-full">
        <VStack @collapsed={{true}}>
          <div class="tio-row-start-between">
            <div class="tio-row">
              {{#if this.cardView.imageUrl}}
                {{! <div class="tio-row-center h-12 w-40 rounded border tio-border-subtle tio-bg-surface"> }}
                <img
                  src="{{this.cardView.imageUrl}}"
                  alt="{{this.cardView.schoolName}}"
                  width="275"
                  height="90"
                />
                {{! </div> }}
              {{/if}}
            </div>
            {{#if @isAdminView}}
              <span class="tio-row-center">
                <button
                  type="button"
                  class="p-1"
                  aria-label="Delete learning partner"
                  {{on "click" this.openDeleteModal}}
                >
                  <PhTrash class="w-6 h-6 hover:cursor-pointer tio-fg-danger" />
                </button>
                <LinkTo
                  @route="authenticated.lpn.edit"
                  @model={{@lpn.id}}
                  aria-label="Edit learning partner"
                >
                  <PhPencil class="tio-fg-info-h w-6 h-6" />
                </LinkTo>
              </span>
            {{/if}}
          </div>

          <div class="tio-wrap-tight min-h-[10px] text-sm tio-fg-secondary">
            {{#if (and @isAdminView (not @lpn.institutionIdentifier) (not @lpn.dapip_id))}}
              <Chip
                @appearance="outlined"
                class="app-status-panel--warning app-status-text-warning"
              >
                <span class="text-xs whitespace-nowrap">{{t "lpn.no_associated_school"}}</span>
              </Chip>
            {{/if}}
            {{#if this.cardView.employerRecommended}}
              <div class="tio-row-flush font-semibold">
                <PhSealCheck @size="20" class="tio-fg-muted" @weight="bold" />
                <span>{{t "lpn.employer_recommended"}}</span>
              </div>
            {{/if}}
            {{#if this.topBadge}}
              <div class="tio-row-flush font-semibold">
                <PhCurrencyCircleDollar @size="20" class="tio-fg-muted" @weight="bold" />
                <span>{{this.topBadge}}</span>
              </div>
            {{/if}}
          </div>

          <h3 class="mt-1 tio-text-xl-semibold tio-fg-primary">{{this.cardView.schoolName}}</h3>

          <div class="min-h-[14px]">
            {{#if this.providedBy}}
              <p class="tio-text-caption font-semibold uppercase tracking-wide">
                {{t "lpn.provided_by"}}
                <span class="tio-fg-secondary">{{this.providedBy}}</span>
              </p>
            {{/if}}
          </div>

          <div class="mt-1 min-h-[84px] tio-text-sm-normal tio-fg-secondary prose prose-sm">
            {{this.descriptionHtml}}
          </div>

          <div class="mt-3 min-h-[20px]">
            {{#if this.tags.length}}
              <div class="tio-wrap-tight">
                {{#each this.tags as |tag|}}
                  <Chip @appearance="outlined" class="tio-bg-sunken tio-fg-secondary">
                    <span class="text-xs whitespace-nowrap">{{tag}}</span>
                  </Chip>
                {{/each}}
              </div>
            {{/if}}
          </div>
        </VStack>
      </Card>
    </ConditionalLink>
    <Modal
      @isOpen={{this.isDeleteModalOpen}}
      @onClose={{this.closeDeleteModal}}
      @ariaLabel={{t "common.aria_labels.delete_partner_confirmation"}}
    >
      <:header>{{t "lpn.delete_partner_confirmation"}}</:header>
      <:body>
        {{t "invoices.permanent_action"}}
      </:body>
      <:footer>
        <Button @appearance="minimal" class="mr-4" {{on "click" this.closeDeleteModal}}>
          {{t "common.cancel"}}
        </Button>
        <Button @intent="danger" {{on "click" this.deletePartner}} @isRunning={{this.isDeleting}}>
          {{t "common.delete"}}
        </Button>
      </:footer>
    </Modal>
  </template>
}
